import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Trans, withTranslation, type WithTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface Props extends WithTranslation {
  children: ReactNode;
  /** `root`: full-screen last resort. `route`: inline inside the app shell so the chrome stays usable. */
  variant?: 'root' | 'route';
  /** Renders a "Home" action when given. */
  onGoHome?: () => void;
  /** A change clears a caught error without remounting a healthy subtree. */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  resetKey: string | undefined;
}

class ErrorBoundaryImpl extends Component<Props, State> {
  state: State = { hasError: false, error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props: Pick<Props, 'resetKey'>, state: State): Partial<State> | null {
    if (props.resetKey === state.resetKey) return null;
    return { hasError: false, error: null, resetKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] render crash', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { t, variant = 'root', onGoHome } = this.props;
    const isRoute = variant === 'route';
    const message = this.state.error?.message ?? '';

    return (
      <div
        className={
          isRoute
            ? 'flex items-center justify-center px-4 py-12'
            : 'min-h-screen flex items-center justify-center bg-background p-6'
        }
      >
        <Alert variant="destructive" className="max-w-xl">
          <AlertTriangle className="size-5" />
          <AlertTitle>{t('common.errors.boundary.title')}</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              <Trans
                i18nKey={
                  isRoute
                    ? 'common.errors.boundary.routeDescription'
                    : 'common.errors.boundary.description'
                }
                components={{ 1: <strong /> }}
              />
            </p>
            {message && (
              <pre className="text-xs bg-muted/50 text-muted-foreground rounded-md p-2 overflow-x-auto">
                {message}
              </pre>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={this.handleReset}>
                {t('common.errors.boundary.reset')}
              </Button>
              {/* Kept on the route screen too: a failed lazy-chunk import is cached by React.lazy, so
                  only a full reload recovers it once the chunk-reload guard has already fired. */}
              <Button size="sm" variant={onGoHome ? 'outline' : 'default'} onClick={this.handleReload}>
                <RefreshCw className="size-4 mr-1.5" aria-hidden />
                {t('common.errors.boundary.reload')}
              </Button>
              {onGoHome && (
                <Button size="sm" onClick={onGoHome}>
                  <Home className="size-4 mr-1.5" aria-hidden />
                  {t('common.errors.boundary.home')}
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }
}

const ErrorBoundary = withTranslation()(ErrorBoundaryImpl);
export default ErrorBoundary;

/**
 * Route-scoped boundary for the routed content area. Resets on every navigation (the location key
 * changes even when "Home" leads to the page that crashed), so a crash never survives leaving the
 * page. Not a React `key`: that would remount nested layouts (settings rail) on every navigation.
 */
export function RouteErrorBoundary({ homePath, children }: { homePath: string; children: ReactNode }) {
  const navigate = useNavigate();
  const { key } = useLocation();
  return (
    <ErrorBoundary variant="route" resetKey={key} onGoHome={() => navigate(homePath)}>
      {children}
    </ErrorBoundary>
  );
}
